import { Action } from '@ngrx/store';
import {
    DELIVERY_INTEGRATIONS_FETCH,
    DELIVERY_INTEGRATIONS_FETCHING_SET,
    DELIVERY_INTEGRATIONS_SET,
    DELIVERY_INTEGRATION_CONNECT,
    DELIVERY_INTEGRATION_DISCONNECT,
    DELIVERY_INTEGRATION_VIEWED_SET,
    DELIVERY_ORDERS_FILTERS_FETCH,
    DELIVERY_ORDERS_FILTERS_SET,
    DELIVERY_ORDERS_POLLING_START,
    DELIVERY_ORDERS_POLLING_STOP,
    DELIVERY_ORDERS_SEARCH,
    DELIVERY_ORDERS_SEARCH_MORE,
    DELIVERY_ORDERS_SUMMARY_SET,
    DELIVERY_ORDERS_TRACKING_FETCH,
    DELIVERY_ORDERS_TRACKING_GROUP_FETCH,
    DELIVERY_ORDERS_TRACKING_GROUP_SET,
    DELIVERY_ORDERS_TRACKING_POLLING_START,
    DELIVERY_ORDERS_TRACKING_POLLING_STOP,
    DELIVERY_ORDER_CANCEL,
    DELIVERY_ORDER_CANCEL_START,
    DELIVERY_ORDER_CONFIG_FETCH,
    DELIVERY_ORDER_CONFIG_FETCHING_SET,
    DELIVERY_ORDER_CONFIG_UPDATE,
    DELIVERY_ORDER_CREATE,
    DELIVERY_ORDER_DISPATCH,
    DELIVERY_ORDER_DISPATCH_FETCH,
    DELIVERY_ORDER_FETCH,
    DELIVERY_ORDER_FETCHING_SET,
    DELIVERY_ORDER_FOCUSED_SET,
    DELIVERY_ORDER_HELP_FETCH,
    DELIVERY_ORDER_MANAGER_NOTE_ADD_START,
    DELIVERY_ORDER_NEW_SET,
    DELIVERY_ORDER_QUOTE_FETCH,
    DELIVERY_ORDER_QUOTE_FETCHING_SET,
    DELIVERY_ORDER_QUOTE_SET,
    DELIVERY_ORDER_REASSIGN,
    DELIVERY_ORDER_REDISPATCH,
    DELIVERY_ORDER_REFUND_REQUEST,
    DELIVERY_ORDER_REFUND_REQUEST_START,
    DELIVERY_ORDER_UPDATE,
    DELIVERY_ORDER_UPDATE_ADDRESS_START,
    DELIVERY_ORDER_VIEWED_SET,
    DISPATCH_CONFIG_FETCHING_SET
} from '../action-types';
import { SearchDeliveryOrdersRequest } from '@core/models/delivery/search-delivery-orders-request';
import {
    FilterSetAction,
    ListItemSetAction,
    ListItemUnsetAction,
    ListItemUnsetAllAction,
    ListMergeAction,
    ListSetAction,
    ListSetFetchingAction,
    ListSetFetchingMoreAction
} from '../shared/list/list.actions';
import { SharedAction } from '../shared/actions/shared-action';
import { GROUP_DELIVERY_ORDERS, GROUP_DELIVERY_ORDER_CONFIGS } from '../state-groups';
import { DELIVERY_ORDER_CONFIG_ENTITY, DELIVERY_ORDER_ENTITY } from '../entity-types';
import { DeliveryOrdersSummary } from '@core/models/delivery/summary/delivery-orders-summary';
import { CreateDeliveryOrderRequest } from '@core/models/delivery/create-delivery-order-request';
import { ObservableAction } from '../shared/actions/observable-action';
import { UpdateDeliveryOrderConfigRequest } from '@core/models/delivery/update-delivery-order-config.request';
import { DeliveryOrder } from '@core/models/delivery/delivery-order';
import { DeliveryOrderGroupTracking } from '@core/models/delivery/tracking/delivery-order-group-tracking';
import { DeliveryOrdersFilters } from '@core/models/delivery/filters/delivery-orders-filters';
import { DeliveryOrderQuote } from '@core/models/delivery/quote/delivery-order-quote';
import { DeliveryOrderRefundRequest } from '@core/models/delivery/refund/delivery-order-refund.request';
import { UpdateDeliveryOrderRequest } from '@core/models/delivery/update-delivery-order.request';
import { DeliveryIntegration } from '@core/models/delivery/integrations/delivery-integration';

export class SearchDeliveryOrdersAction implements Action {
    readonly type = DELIVERY_ORDERS_SEARCH;

    constructor(
        public payload?: {
            request?: SearchDeliveryOrdersRequest;
            perPage?: number;
            isFetching?: boolean;
            ignoreRemotePreferencesUpdate?: boolean;
        }
    ) {}
}

export class SearchMoreDeliveryOrdersAction implements Action {
    readonly type = DELIVERY_ORDERS_SEARCH_MORE;
}

export class ResetDeliveryOrdersAction extends ListItemUnsetAllAction implements SharedAction {
    name = GROUP_DELIVERY_ORDERS;

    constructor() {
        super({ domain: DELIVERY_ORDER_ENTITY });
    }
}

export class SetDeliveryOrdersAction extends ListSetAction implements SharedAction {
    name = GROUP_DELIVERY_ORDERS;

    constructor(payload: any) {
        super(payload);
        payload.domain = DELIVERY_ORDER_ENTITY;
    }
}

export class MergeDeliveryOrdersAction extends ListMergeAction implements SharedAction {
    name = GROUP_DELIVERY_ORDERS;

    constructor(payload: any) {
        super(payload);
        payload.domain = DELIVERY_ORDER_ENTITY;
    }
}

export class SetDeliveryOrdersFetchingAction
    extends ListSetFetchingAction
    implements SharedAction {
    name = GROUP_DELIVERY_ORDERS;

    constructor(payload: any) {
        super(payload);
    }
}

export class SetDeliveryOrdersFetchingMoreAction
    extends ListSetFetchingMoreAction
    implements SharedAction {
    name = GROUP_DELIVERY_ORDERS;

    constructor(payload: any) {
        super(payload);
    }
}

export class SetDeliveryOrdersFilterByAction
    extends FilterSetAction
    implements SharedAction {
    name = GROUP_DELIVERY_ORDERS;

    constructor(payload: any) {
        super(payload);
        payload.domain = DELIVERY_ORDER_ENTITY;
    }
}

export class SetDeliveryOrdersSummaryAction implements Action {
    readonly type = DELIVERY_ORDERS_SUMMARY_SET;

    constructor(
        public payload: {
            summary: DeliveryOrdersSummary;
        }
    ) {}
}

export class SetDeliveryOrderConfigAction
    extends ListItemSetAction
    implements SharedAction {
    name = GROUP_DELIVERY_ORDER_CONFIGS;

    constructor(payload: any) {
        super(payload);
        payload.domain = DELIVERY_ORDER_CONFIG_ENTITY;
    }
}

export class SetNewDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_NEW_SET;

    constructor(
        public payload: {
            order: CreateDeliveryOrderRequest;
        }
    ) {}
}

@ObservableAction()
export class FetchDeliveryOrderConfigAction implements Action {
    readonly type = DELIVERY_ORDER_CONFIG_FETCH;

    constructor(
        public payload: {
            locationId: string;
        }
    ) {}
}

export class SetDeliveryOrderConfigFetchingAction implements Action {
    readonly type = DELIVERY_ORDER_CONFIG_FETCHING_SET;

    constructor(
        public payload: {
            value: boolean;
        }
    ) {}
}

export class SetDispatchConfigFetchingAction implements Action {
    readonly type = DISPATCH_CONFIG_FETCHING_SET;

    constructor(
        public payload: {
            value: boolean;
        }
    ) {}
}

@ObservableAction()
export class UpdateDeliveryOrderConfigAction implements Action {
    readonly type = DELIVERY_ORDER_CONFIG_UPDATE;

    constructor(
        public payload: {
            locationId: string;
            request: UpdateDeliveryOrderConfigRequest;
        }
    ) {}
}

@ObservableAction()
export class DispatchDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_DISPATCH;

    constructor(
        public payload: {
            orderId: string;
            order: DeliveryOrder;
        }
    ) {}
}

export class SetDeliveryOrderFetchingAction implements Action {
    readonly type = DELIVERY_ORDER_FETCHING_SET;

    constructor(public payload: { value: boolean }) {}
}

export class SetDeliveryOrderAction extends ListItemSetAction implements SharedAction {
    name = GROUP_DELIVERY_ORDERS;

    constructor(payload: any) {
        super(payload);
        payload.domain = DELIVERY_ORDER_ENTITY;
    }
}

export class UnsetDeliveryOrderAction
    extends ListItemUnsetAction
    implements SharedAction {
    name = GROUP_DELIVERY_ORDERS;

    constructor(payload: any) {
        super(payload);
        payload.domain = DELIVERY_ORDER_ENTITY;
    }
}

export class StartDeliveryOrdersPollingAction implements Action {
    readonly type = DELIVERY_ORDERS_POLLING_START;
}

export class StartDeliveryTrackingPollingAction implements Action {
    readonly type = DELIVERY_ORDERS_TRACKING_POLLING_START;
}

export class StopDeliveryOrdersPollingAction implements Action {
    readonly type = DELIVERY_ORDERS_POLLING_STOP;
}

export class StopDeliveryTrackingPollingAction implements Action {
    readonly type = DELIVERY_ORDERS_TRACKING_POLLING_STOP;
}

export class FetchDeliveryOrdersFiltersAction implements Action {
    readonly type = DELIVERY_ORDERS_FILTERS_FETCH;
}

@ObservableAction()
export class FetchDispatchOrderAction implements Action {
    readonly type = DELIVERY_ORDER_DISPATCH_FETCH;

    constructor(public payload: { orderId: string }) {}
}

export class FetchDeliveryOrderGroupTrackingAction implements Action {
    readonly type = DELIVERY_ORDERS_TRACKING_GROUP_FETCH;

    constructor(public payload: { locationId: string }) {}
}

export class SetDeliveryOrderGroupTrackingAction implements Action {
    readonly type = DELIVERY_ORDERS_TRACKING_GROUP_SET;

    constructor(public payload: { tracking: DeliveryOrderGroupTracking }) {}
}

@ObservableAction()
export class FetchDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_FETCH;

    constructor(public payload: { orderId: string }) {}
}

export class SetIsDeliveryOrderOnFocusAction implements Action {
    readonly type = DELIVERY_ORDER_FOCUSED_SET;

    constructor(
        public payload: {
            value: boolean;
        }
    ) {}
}

export class FetchDeliveryOrderTrackingAction implements Action {
    readonly type = DELIVERY_ORDERS_TRACKING_FETCH;

    constructor(public payload: { orderId: string, delay?: number }) {}
}

@ObservableAction()
export class FetchDeliveryOrderHelpAction implements Action {
    readonly type = DELIVERY_ORDER_HELP_FETCH;

    constructor(public payload: { orderId: string }) {}
}

@ObservableAction()
export class FetchDeliveryOrderQuoteAction implements Action {
    readonly type = DELIVERY_ORDER_QUOTE_FETCH;

    constructor(
        public payload?: {
            useNewOrder?: boolean;
            pickupRequiredAt?: string;
            deliveryTimeUtc?: string;
        }
    ) {}
}

export class SetDeliveryOrdersFiltersAction implements Action {
    readonly type = DELIVERY_ORDERS_FILTERS_SET;

    constructor(
        public payload: {
            filters: DeliveryOrdersFilters;
        }
    ) {}
}

export class SetViewedDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_VIEWED_SET;

    constructor(
        public payload: {
            orderId: string;
        }
    ) {}
}

export class SetDeliveryOrderQuotesAction implements Action {
    readonly type = DELIVERY_ORDER_QUOTE_SET;

    constructor(
        public payload: {
            orderId: string;
            quotes: DeliveryOrderQuote[];
        }
    ) {}
}

export class SetIsFetchingDeliveryOrderQuotesAction implements Action {
    readonly type = DELIVERY_ORDER_QUOTE_FETCHING_SET;

    constructor(
        public payload: {
            value: boolean;
        }
    ) {}
}

@ObservableAction()
export class CreateDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_CREATE;

    constructor(
        public payload: {
            request: CreateDeliveryOrderRequest;
        }
    ) {}
}

@ObservableAction()
export class UpdateDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_UPDATE;

    constructor(
        public payload: {
            orderId: string;
            request: UpdateDeliveryOrderRequest;
        }
    ) {}
}

@ObservableAction()
export class CancelDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_CANCEL;

    constructor(
        public payload: {
            orderId: string;
            isRedispatch?: boolean;
        }
    ) {}
}

@ObservableAction()
export class StartCancelDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_CANCEL_START;

    constructor(
        public payload: {
            order: DeliveryOrder;
        }
    ) {}
}

@ObservableAction()
export class StartUpdateDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_UPDATE_ADDRESS_START;

    constructor(
        public payload: {
            order: DeliveryOrder;
        }
    ) {}
}

@ObservableAction()
export class ReassignDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_REASSIGN;

    constructor(
        public payload: {
            id: string,
            orderId: string;
            driverId: string;
            autoAssign: boolean;
        }
    ) {}
}

@ObservableAction()
export class RedispatchDeliveryOrderAction implements Action {
    readonly type = DELIVERY_ORDER_REDISPATCH;

    constructor(
        public payload: {
            orderId: string;
            order: DeliveryOrder;
        }
    ) {}
}

@ObservableAction()
export class StartRefundRequestAction implements Action {
    readonly type = DELIVERY_ORDER_REFUND_REQUEST_START;

    constructor(
        public payload: {
            order: DeliveryOrder;
        }
    ) {}
}

@ObservableAction()
export class RefundRequestAction implements Action {
    readonly type = DELIVERY_ORDER_REFUND_REQUEST;

    constructor(
        public payload: {
            order: DeliveryOrder;
            request: DeliveryOrderRefundRequest;
        }
    ) {}
}

@ObservableAction()
export class StartAddManagerNoteAction implements Action {
    readonly type = DELIVERY_ORDER_MANAGER_NOTE_ADD_START;

    constructor(
        public payload: {
            order: DeliveryOrder;
        }
    ) {}
}

export class FetchDeliveryIntegrationsAction implements Action {
    readonly type = DELIVERY_INTEGRATIONS_FETCH;

    constructor(
        public payload: {
            locationId: string;
        }
    ) {}
}

export class SetDeliveryIntegrationsAction implements Action {
    readonly type = DELIVERY_INTEGRATIONS_SET;

    constructor(
        public payload: {
            integrations: { [provider: string]: DeliveryIntegration };
        }
    ) {}
}

export class SetDeliveryIntegrationsIsFetching implements Action {
    readonly type = DELIVERY_INTEGRATIONS_FETCHING_SET;

    constructor(
        public payload: {
            value: boolean;
        }
    ) {}
}

export class SetViewedDeliveryIntegrationAction implements Action {
    readonly type = DELIVERY_INTEGRATION_VIEWED_SET;

    constructor(
        public payload: {
            provider: string;
        }
    ) {}
}

@ObservableAction()
export class ConnectDeliveryIntegrationAction implements Action {
    readonly type = DELIVERY_INTEGRATION_CONNECT;

    constructor(
        public payload: {
            provider: string;
        }
    ) {}
}

@ObservableAction()
export class DisconnectDeliveryIntegrationAction implements Action {
    readonly type = DELIVERY_INTEGRATION_DISCONNECT;

    constructor(
        public payload: {
            id: string;
        }
    ) {}
}
